import React, { useState, useEffect } from "react";
import { Button, Badge } from "../ui";
import { MapPin, CheckCircle2, Gift, Ticket, Route, Calendar, Shirt, FileText, Headset, Download, Loader2 } from "lucide-react";
import { formatDateBR } from "../../utils/dateUtils";
import { obterLoteAtivoEDisponivel, isFaixaEtaria60Mais } from "../../utils/loteUtils";
import MapaTrajetoCorrida from "../mapa/MapaTrajetoCorrida";
import { API_BASE_URL } from "../../services/config";
import NovoEventoService from "../../services/Eventos/NovoEventoService";

function SideBarDetailCorrida({ produto, onInscricao }) {
    const [baixandoRegulamento, setBaixandoRegulamento] = useState(false);
    const [trajetoApi, setTrajetoApi] = useState(null);

    const idCorrida = produto?.id || produto?.id_corrida || produto?.pk;

    useEffect(() => {
        let isMounted = true;
        const carregarTrajeto = async () => {
            if (!idCorrida) return;
            try {
                const eventoService = new NovoEventoService();
                const res = await eventoService.getTrajeto(idCorrida);
                if (res && res.ok && isMounted) {
                    const data = await res.json();
                    setTrajetoApi(data);
                }
            } catch (err) {
                console.warn("Aviso ao carregar dados do percurso/trajeto:", err);
            }
        };

        carregarTrajeto();

        return () => {
            isMounted = false;
        };
    }, [idCorrida]);

    const getBadgeVariant = (status) => {
        switch (status) {
            case 'Em Preparação':
                return 'info';
            case 'Inscrição Aberta':
                return 'success';
            case 'Inscrição Encerrada':
                return 'warning';
            case 'Cancelado':
                return 'danger';
            case 'Finalizado':
                return 'secondary';
            default:
                return 'secondary';
        }
    };

    const toDate = (value) => {
        if (!value) return null;
        const parsed = value instanceof Date ? value : new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const formatDate = (value) => {
        return formatDateBR(value);
    };

    const formatTime = (value) => {
        if (!value) return "";
        if (typeof value === "string") {
            const timeOnly = value.match(/^(\d{2}):(\d{2})(?::\d{2})?$/);
            if (timeOnly) {
                return `${timeOnly[1]}:${timeOnly[2]}`;
            }
        }
        const date = toDate(value);
        return date ? date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "";
    };

    if (!produto) return null;

    const loteAtivo = (Array.isArray(produto.lotes) ? obterLoteAtivoEDisponivel(produto.lotes) : null) || produto.lote_ativo || null;
    const valorDisplay = loteAtivo ? loteAtivo.valor : produto.valor_inscricao;

    const statusBloqueadoExplicitamente =
        produto.status_evento === 'Inscrição Encerrada' ||
        produto.status_evento === 'Cancelado' ||
        produto.status_evento === 'Finalizado';

    const inscricaoAbertaPorDatas = (() => {
        if (!produto.inicio_inscricao || !produto.fim_inscricao) return false;
        const d = new Date();
        const hojeStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const inicio = String(produto.inicio_inscricao).split('T')[0];
        const fim = String(produto.fim_inscricao).split('T')[0];
        return inicio <= hojeStr && hojeStr <= fim;
    })();

    const inscricaoAberta =
        !statusBloqueadoExplicitamente &&
        (produto.status_evento === 'Inscrição Aberta' || inscricaoAbertaPorDatas);

    // Helpers e dados para a seção "Informações do Atleta"
    const percursoDisplay = (() => {
        // Prioriza o tamanho_percurso retornado pela API ou diretamente pelo produto
        const tamanho = trajetoApi?.tamanho_percurso ?? produto?.tamanho_percurso ?? trajetoApi?.distancia_km ?? produto?.trajeto_mapa?.distancia_km ?? produto?.distancia_km;
        if (tamanho) {
            return `${tamanho} KM`;
        }
        return "5 KM";
    })();

    const googleMapsUrl = (() => {
        if (produto.link_google_maps) return produto.link_google_maps;
        if (produto.link_trajeto && typeof produto.link_trajeto === 'string' && produto.link_trajeto.startsWith('http')) return produto.link_trajeto;
        if (trajetoApi?.url_google_maps) return trajetoApi.url_google_maps;
        if (produto.trajeto_mapa?.url_google_maps) return produto.trajeto_mapa.url_google_maps;
        const ponto = trajetoApi?.ponto_largada || produto.trajeto_mapa?.ponto_largada;
        if (ponto?.lat && ponto?.lng) {
            return `https://www.google.com/maps/search/?api=1&query=${ponto.lat},${ponto.lng}`;
        }
        const query = [produto.nome_corrida, produto.cidade_evento, produto.uf].filter(Boolean).join(", ");
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query || "corrida")}`;
    })();

    const retiradaKitDisplay = produto.retirada_kit
        || produto.data_retirada_kit
        || (produto.dt_entrega_kit ? formatDate(produto.dt_entrega_kit) : null)
        || "26 e 27/11/2026";

    const kitAtletaDisplay = produto.kit_atleta
        || produto.descricao_kit
        || produto.itens_kit
        || "Camisa + Medalha";

    const urlEndpointRegulamento = idCorrida
        ? `${API_BASE_URL.replace(/\/+$/, '')}/core/registro_corrida/${idCorrida}/regulamento/`
        : null;

    const handleDownloadRegulamento = async (e) => {
        if (e) e.preventDefault();
        if (!idCorrida) {
            if (produto.regulamento || produto.link_regulamento || produto.url_regulamento) {
                window.open(produto.regulamento || produto.link_regulamento || produto.url_regulamento, '_blank');
                return;
            }
            alert("ID da corrida não disponível para download do regulamento.");
            return;
        }

        try {
            setBaixandoRegulamento(true);
            const token = sessionStorage.getItem('brv-user-token');
            const headers = {};
            if (token) {
                headers['Authorization'] = `Token ${token}`;
            }

            const response = await fetch(urlEndpointRegulamento, {
                method: 'GET',
                headers
            });

            if (!response.ok) {
                // Se o endpoint retornar 404 ou erro, tenta fallback para URL direta no produto se existir
                if (produto.regulamento || produto.link_regulamento || produto.url_regulamento) {
                    window.open(produto.regulamento || produto.link_regulamento || produto.url_regulamento, '_blank');
                    return;
                }
                throw new Error("Não foi possível baixar o regulamento.");
            }

            // Verifica se a resposta é JSON com url ou o próprio arquivo blob
            const contentType = response.headers.get("content-type") || "";
            if (contentType.includes("application/json")) {
                const data = await response.json();
                const fileUrl = data.url || data.regulamento || data.link || data.arquivo;
                if (fileUrl) {
                    window.open(fileUrl, '_blank');
                } else {
                    alert("Regulamento não disponível no momento.");
                }
            } else {
                const blob = await response.blob();
                const blobUrl = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = blobUrl;
                // Tenta extrair filename do content-disposition
                const disposition = response.headers.get("content-disposition");
                let filename = `regulamento_${produto.nome_corrida || idCorrida}.pdf`;
                if (disposition && disposition.includes("filename=")) {
                    const match = disposition.match(/filename=["']?([^"']+)["']?/);
                    if (match && match[1]) filename = match[1];
                }
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(blobUrl);
            }
        } catch (error) {
            console.error("Erro ao baixar regulamento:", error);
            if (produto.regulamento || produto.link_regulamento || produto.url_regulamento) {
                window.open(produto.regulamento || produto.link_regulamento || produto.url_regulamento, '_blank');
            } else {
                alert("O regulamento ainda não foi disponibilizado para este evento.");
            }
        } finally {
            setBaixandoRegulamento(false);
        }
    };

    const regulamentoUrl = produto.regulamento || produto.link_regulamento || produto.url_regulamento || null;

    const suporteNome = produto.suporte_nome || produto.nome_suporte || "WhatsApp URBRACE";
    const telefoneSuporte = produto.whatsapp_suporte || produto.telefone_suporte || produto.whatsapp || "(96) 98100-4733";
    const whatsAppUrl = (() => {
        const cleanNumber = String(telefoneSuporte).replace(/\D/g, "");
        const numberFormatted = cleanNumber.length <= 11 ? `55${cleanNumber}` : cleanNumber;
        return `https://wa.me/${numberFormatted}`;
    })();

    return (
        <div className="w-full p-2 pb-28 md:p-3 md:pb-28 lg:p-4">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Coluna Principal / Esquerda: Imagem, Cabeçalho, Metadados e Descrição */}
                <div className="lg:col-span-7 flex flex-col gap-4">
                    <img
                        src={produto.folder || "https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?auto=format&fit=crop&w=1200&q=80"}
                        alt={produto.nome_corrida}
                        className="w-full h-56 md:h-72 lg:h-96 object-cover rounded-xl border border-urb-border"
                    />

                    <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                            <Badge variant={getBadgeVariant(produto.status_evento)}>
                                {produto.status_evento}
                            </Badge>
                            {produto.is_gratuita && (
                                <Badge variant="success" className="font-bold">
                                    Gratuita
                                </Badge>
                            )}
                        </div>
                        <h2 className="text-2xl lg:text-3xl font-bold text-white m-0 mt-2">{produto.nome_corrida}</h2>
                        <span className="text-sm text-slate-400 flex items-center gap-2 mt-1">
                            <MapPin className="w-4 h-4 text-urb-primary shrink-0" />
                            {produto.cidade_evento}{produto.uf ? ` - ${produto.uf}` : ''}
                        </span>
                    </div>

                    <div className="flex flex-col gap-2.5 border border-urb-border p-4 rounded-xl bg-urb-card text-base">
                        <div className="flex justify-between items-center text-xs sm:text-sm">
                            <span className="text-slate-400">Início das Inscrições:</span>
                            <span className="font-medium text-white">{formatDate(produto.inicio_inscricao)}</span>
                        </div>
                        <div className="flex justify-between items-center text-xs sm:text-sm">
                            <span className="text-slate-400">Fim das Inscrições:</span>
                            <span className="font-medium text-white">{formatDate(produto.fim_inscricao)}</span>
                        </div>
                        {produto.dt_entrega_kit && (
                            <div className="flex justify-between items-center text-xs sm:text-sm">
                                <span className="text-slate-400">Entrega do Kit:</span>
                                <span className="font-medium text-white">{formatDate(produto.dt_entrega_kit)}</span>
                            </div>
                        )}
                        <div className="flex justify-between items-center text-xs sm:text-sm">
                            <span className="text-slate-400">Data do Evento:</span>
                            <span className="font-bold text-urb-primary">
                                {formatDate(produto.data_evento)} {produto.hora_inicio_evento ? `às ${formatTime(produto.hora_inicio_evento)}` : ''}
                            </span>
                        </div>
                        {produto.tamanho_percurso && (
                            <div className="flex justify-between items-center text-xs sm:text-sm">
                                <span className="text-slate-400">Tamanho do percurso:</span>
                                <span className="font-medium text-white">{produto.tamanho_percurso} KM</span>
                            </div>
                        )}
                    </div>

                    {produto.trajeto && (
                        <div className="flex flex-col gap-2">
                            <span className="font-bold text-sm lg:text-base text-white">Sobre o Evento</span>
                            <p className="text-xs sm:text-sm text-slate-400 m-0 leading-relaxed">
                                {produto.trajeto}
                            </p>
                        </div>
                    )}

                    {/* Percurso no Google Maps */}
                    {produto.trajeto_mapa && (
                        <div className="flex flex-col gap-2">
                            <span className="font-bold text-sm lg:text-base text-white flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-urb-primary" />
                                Percurso no Google Maps
                            </span>
                            <MapaTrajetoCorrida trajeto={produto.trajeto_mapa} altura="300px" />
                        </div>
                    )}
                </div>

                {/* Coluna 2: Informações do Atleta, Categorias, Lotes e Ação de Inscrição */}
                <div className="lg:col-span-5 flex flex-col justify-between border-t lg:border-t-0 lg:border-l border-urb-border pt-4 lg:pt-0 lg:pl-6">
                    <div className="flex flex-col gap-5">
                        {/* Informações do Atleta */}
                        <div className="flex flex-col gap-3">
                            <h3 className="text-lg lg:text-xl font-bold text-white m-0">Informações do Atleta</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {/* Card 1: Percurso */}
                                <div className="flex items-start gap-3 p-3.5 rounded-2xl bg-urb-card border border-urb-border hover:border-urb-primary/40 transition-colors">
                                    <Route className="w-8 h-8 text-urb-primary shrink-0 mt-0.5" />
                                    <div className="flex-1 min-w-0 flex flex-col items-start">
                                        <span className="font-bold text-white text-sm leading-tight">Percurso</span>
                                        <span className="font-semibold text-white text-xs mt-0.5">{percursoDisplay}</span>
                                        <p className="text-[11px] text-slate-400 mt-1 mb-0 leading-relaxed">
                                            Confira o mapa do percurso com todos os detalhes.
                                        </p>
                                        <a
                                            href={googleMapsUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 px-3 py-1 mt-2.5 rounded-full border border-urb-primary text-urb-primary hover:bg-urb-primary/10 transition-colors text-[11px] font-semibold no-underline"
                                        >
                                            <MapPin className="w-3 h-3 text-urb-primary shrink-0" />
                                            <span>Abrir no Google Maps</span>
                                        </a>
                                    </div>
                                </div>

                                {/* Card 2: Retirada de Kit */}
                                <div className="flex items-start gap-3 p-3.5 rounded-2xl bg-urb-card border border-urb-border hover:border-urb-primary/40 transition-colors">
                                    <Calendar className="w-8 h-8 text-urb-primary shrink-0 mt-0.5" />
                                    <div className="flex-1 min-w-0 flex flex-col items-start">
                                        <span className="font-bold text-white text-sm leading-tight">Retirada de Kit</span>
                                        <span className="font-semibold text-white text-xs mt-0.5">{retiradaKitDisplay}</span>
                                        <p className="text-[11px] text-slate-400 mt-1 mb-0 leading-relaxed">
                                            Consulte horário e local de entrega.
                                        </p>
                                    </div>
                                </div>

                                {/* Card 3: Kit do Atleta */}
                                <div className="flex items-start gap-3 p-3.5 rounded-2xl bg-urb-card border border-urb-border hover:border-urb-primary/40 transition-colors">
                                    <Shirt className="w-8 h-8 text-urb-primary shrink-0 mt-0.5" />
                                    <div className="flex-1 min-w-0 flex flex-col items-start">
                                        <span className="font-bold text-white text-sm leading-tight">Kit do Atleta</span>
                                        <span className="font-semibold text-white text-xs mt-0.5">{kitAtletaDisplay}</span>
                                        <p className="text-[11px] text-slate-400 mt-1 mb-0 leading-relaxed">
                                            Um kit exclusivo para marcar sua missão.
                                        </p>
                                    </div>
                                </div>

                                {/* Card 4: Regulamento */}
                                <div className="flex items-start gap-3 p-3.5 rounded-2xl bg-urb-card border border-urb-border hover:border-urb-primary/40 transition-colors">
                                    <FileText className="w-8 h-8 text-urb-primary shrink-0 mt-0.5" />
                                    <div className="flex-1 min-w-0 flex flex-col items-start">
                                        <span className="font-bold text-white text-sm leading-tight">Regulamento</span>
                                        <p className="text-[11px] text-slate-400 mt-1 mb-0 leading-relaxed">
                                            Leia o regulamento completo do evento.
                                        </p>
                                        <a
                                            href={urlEndpointRegulamento || regulamentoUrl || "#"}
                                            onClick={handleDownloadRegulamento}
                                            role="link"
                                            aria-disabled={baixandoRegulamento}
                                            className={`inline-flex items-center gap-1.5 px-3 py-1 mt-2.5 rounded-full border border-urb-primary text-urb-primary hover:bg-urb-primary/10 transition-colors text-[11px] font-semibold no-underline cursor-pointer ${baixandoRegulamento ? "opacity-50 pointer-events-none" : ""}`}
                                        >
                                            {baixandoRegulamento ? (
                                                <Loader2 className="w-3 h-3 text-urb-primary animate-spin shrink-0" />
                                            ) : (
                                                <FileText className="w-3 h-3 text-urb-primary shrink-0" />
                                            )}
                                            <span>{baixandoRegulamento ? "Baixando..." : "Ver regulamento"}</span>
                                        </a>
                                    </div>
                                </div>

                                {/* Card 5: Suporte */}
                                <div className="flex items-start gap-3 p-3.5 rounded-2xl bg-urb-card border border-urb-border hover:border-urb-primary/40 transition-colors sm:col-span-2">
                                    <Headset className="w-8 h-8 text-urb-primary shrink-0 mt-0.5" />
                                    <div className="flex-1 min-w-0 flex flex-col items-start">
                                        <span className="font-bold text-white text-sm leading-tight">Suporte</span>
                                        <span className="font-semibold text-white text-xs mt-0.5">{suporteNome}</span>
                                        <a
                                            href={whatsAppUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 mt-2 text-white hover:text-urb-primary transition-colors no-underline group"
                                        >
                                            <svg className="w-4 h-4 text-urb-primary shrink-0 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.983zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                                            </svg>
                                            <span className="text-xs sm:text-sm font-semibold text-slate-200 group-hover:text-urb-primary transition-colors">
                                                {telefoneSuporte}
                                            </span>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {produto.categorias && produto.categorias.length > 0 && (
                            <div className="flex flex-col gap-2">
                                <span className="font-bold text-sm lg:text-base text-white">Categorias Disponíveis</span>
                                <div className="flex flex-col gap-2 max-h-48 overflow-y-auto pr-1">
                                    {produto.categorias.map((cat) => {
                                        const is60 = !produto.is_gratuita && isFaixaEtaria60Mais(cat.faixa_etaria);
                                        let textoFaixa = '';
                                        if (cat.faixa_etaria) {
                                            const ft = String(cat.faixa_etaria).trim();
                                            const jaTemAnos = /anos?/i.test(ft) || /livre|geral/i.test(ft);
                                            textoFaixa = jaTemAnos ? `Faixa: ${ft}` : `Idade: ${ft} anos`;
                                        } else if (cat.idade_minima !== undefined && cat.idade_maxima !== undefined) {
                                            textoFaixa = `Idade: ${cat.idade_minima} a ${cat.idade_maxima} anos`;
                                        }

                                        return (
                                            <div key={cat.id} className="flex items-center justify-between p-2.5 rounded-lg text-xs border border-urb-border bg-urb-card">
                                                <div className="flex flex-col gap-0.5">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-semibold text-white text-sm">{cat.categoria || cat.nome_categoria}</span>
                                                        {is60 && (
                                                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                                                                50% OFF (60+)
                                                            </span>
                                                        )}
                                                    </div>
                                                    {textoFaixa && (
                                                        <span className="text-slate-400 text-xs">
                                                            {textoFaixa}
                                                        </span>
                                                    )}
                                                </div>
                                                <CheckCircle2 className="w-4 h-4 text-urb-primary shrink-0" />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {produto.is_gratuita ? (
                            <div className="flex items-center gap-3 p-3.5 rounded-xl border border-urb-primary/30 bg-urb-primary/10">
                                <Gift className="w-6 h-6 text-urb-primary shrink-0" />
                                <div className="flex flex-col">
                                    <span className="font-bold text-white text-sm">Evento Gratuito</span>
                                    <span className="text-slate-400 text-xs">Esta corrida não possui cobrança de lotes. Inscrição 100% gratuita!</span>
                                </div>
                            </div>
                        ) : (
                            produto.lotes && produto.lotes.length > 0 && (
                                <div className="flex flex-col gap-2">
                                    <span className="font-bold text-sm lg:text-base text-white">Lotes de Inscrição</span>
                                    <div className="flex flex-col gap-2 max-h-60 overflow-y-auto pr-1">
                                        {produto.lotes.map((lote) => {
                                            const isAtivo = lote.esta_ativo || (loteAtivo && loteAtivo.id === lote.id);
                                            return (
                                                <div 
                                                    key={lote.id} 
                                                    className={`flex items-center justify-between p-2.5 rounded-lg text-xs border transition-colors ${
                                                        isAtivo 
                                                            ? "bg-urb-primary/10 border-urb-primary/40" 
                                                            : "bg-urb-card border-urb-border"
                                                    }`}
                                                >
                                                    <div className="flex flex-col gap-0.5">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-semibold text-white text-sm">
                                                                {lote.nome_lote}
                                                            </span>
                                                            {isAtivo && (
                                                                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-bold bg-urb-primary/20 text-urb-primary">
                                                                    VIGENTE
                                                                </span>
                                                            )}
                                                        </div>
                                                        <span className="text-slate-400 text-xs">
                                                            {formatDate(lote.data_inicio)} até {formatDate(lote.data_fim)}
                                                        </span>
                                                    </div>
                                                    <span className={`font-bold text-sm ${isAtivo ? "text-urb-primary" : "text-white"}`}>
                                                        R$ {Number(lote.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )
                        )}
                    </div>

                    <div className="fixed bottom-0 inset-x-0 z-30 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] bg-urb-card/95 backdrop-blur-md border-t border-urb-border shadow-[0_-10px_30px_rgba(0,0,0,0.6)] animate-in slide-in-from-bottom duration-300 lg:static lg:z-auto lg:p-0 lg:pt-4 lg:mt-6 lg:bg-transparent lg:backdrop-blur-none lg:shadow-none flex flex-col gap-3">
                        <div className="flex justify-between items-center mb-1">
                            <div className="flex flex-col">
                                <span className="text-xs text-slate-400 uppercase tracking-wider">{produto.is_gratuita ? "INSCRIÇÃO" : "VALOR DA INSCRIÇÃO"}</span>
                                {produto.is_gratuita ? (
                                    <span className="text-xs font-semibold text-urb-primary">
                                        Evento Gratuito
                                    </span>
                                ) : loteAtivo && (
                                    <span className="text-xs font-semibold text-urb-primary">
                                        {loteAtivo.nome_lote}
                                    </span>
                                )}
                            </div>
                            <div>
                                {produto.is_gratuita ? (
                                    <Badge variant="success" className="text-base font-bold px-3 py-1">
                                        Gratuita
                                    </Badge>
                                ) : valorDisplay ? (
                                    <span className="text-2xl lg:text-3xl font-bold text-white">
                                        R$ {Number(valorDisplay).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                ) : (
                                    <span className="text-xs font-semibold text-slate-400">
                                        Inscrições indisponíveis no momento
                                    </span>
                                )}
                            </div>
                        </div>
                        {inscricaoAberta && (produto.is_gratuita || valorDisplay || (Array.isArray(produto.lotes) && produto.lotes.length > 0)) ? (
                            <Button
                                onClick={onInscricao}
                                className="w-full py-3.5 font-bold text-base shadow-neon flex items-center justify-center gap-2"
                            >
                                <Ticket className="w-5 h-5" />
                                {produto.is_gratuita ? "INSCREVER-SE GRATUITAMENTE" : "FAZER INSCRIÇÃO"}
                            </Button>
                        ) : (
                            <Button
                                disabled
                                variant="secondary"
                                className="w-full py-3.5 font-bold text-base opacity-60 cursor-not-allowed"
                            >
                                {statusBloqueadoExplicitamente
                                    ? "INSCRIÇÕES ENCERRADAS"
                                    : (!produto.is_gratuita && !valorDisplay && (!produto.lotes || produto.lotes.length === 0))
                                        ? "INSCRIÇÕES INDISPONÍVEIS"
                                        : "PREPARAÇÃO EM ANDAMENTO"}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SideBarDetailCorrida;
